import { FC, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForgotPassword, ApiError } from '@centrix/api-client';
import {
  Alert,
  Button,
  CollapsibleContainer,
  FormInputField,
  Heading,
  Paragraph,
  Spinner,
} from '@centrix/shared-ui';

import { AuthLayout } from '@/components/AuthLayout';
import { validateEmail } from '@/utils/authValidation';

const backToLoginLink = (
  <Link
    to="/login"
    className="mt-3 block text-center text-sm leading-5 font-medium text-brand-primary hover:underline"
  >
    Back to Login
  </Link>
);

export const ForgotPasswordPage: FC = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const forgotPassword = useForgotPassword();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const trimmedEmail = email.trim();
    const validationError = validateEmail(trimmedEmail);
    if (validationError) {
      setEmailError(validationError);
      return;
    }
    setEmailError(undefined);

    try {
      await forgotPassword.mutateAsync({ email: trimmedEmail });
      // The backend always responds 200 (to prevent account enumeration), so we
      // show the same neutral confirmation whether or not the email exists.
      setSubmittedEmail(trimmedEmail);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const data = err.data as { email?: string[] } | undefined;
        setEmailError(data?.email?.[0] ?? err.message);
      } else if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError('Unable to send the reset link right now. Please try again.');
      }
    }
  };

  const isSubmitting = forgotPassword.isPending;

  if (submittedEmail) {
    return (
      <AuthLayout>
        <Heading as="h2" className="text-[22px] leading-7 font-medium text-black mb-2">
          Check your email
        </Heading>
        <Paragraph className="text-sm leading-6 text-gray-body mb-6">
          If an account exists for <span className="font-medium text-black">{submittedEmail}</span>,
          we&apos;ve sent a link to reset your password. The link expires shortly, so use it soon.
        </Paragraph>
        {backToLoginLink}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Heading as="h2" className="text-[22px] leading-7 font-medium text-black mb-2">
        Reset your password
      </Heading>
      <Paragraph className="text-sm leading-6 text-gray-body mb-6">
        Enter your email address and we&apos;ll send you a link to reset your password.
      </Paragraph>

      <CollapsibleContainer isOpen={!!formError} className="mb-4">
        {formError && (
          <Alert variant="destructive" onDismiss={() => setFormError(null)}>
            {formError}
          </Alert>
        )}
      </CollapsibleContainer>

      <form onSubmit={handleSubmit} className="space-y-6">
        <FormInputField
          id="email"
          type="email"
          label="Email"
          name="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(undefined);
          }}
          placeholder="Enter your registered email address"
          disabled={isSubmitting}
          autoComplete="email"
          error={emailError}
          inputClassName="h-11 px-4 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 disabled:opacity-100 disabled:bg-gray-100 border-gray-placeholder"
        />

        <Button
          type="submit"
          variant="auth"
          size="auth"
          disabled={isSubmitting}
          className="w-full leading-6"
        >
          {isSubmitting ? (
            <>
              <Spinner className="text-white" />
              <span>Sending...</span>
            </>
          ) : (
            'Send Reset Link'
          )}
        </Button>
      </form>

      {backToLoginLink}
    </AuthLayout>
  );
};
